import { Injectable } from '@angular/core';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { catchError, from, Observable, of, throwError } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { AccountFetchService } from '../accounts/account-fetch.service';
import { ApiService, ResourceNotLoadedError } from '@mode/shared/util-halogen';
import { getEmbedded, getEmbeddedCollection } from '@mode/shared/util-halogen';
import { toDatasource, toPublicDatasource } from '../data-mappers/datasource-data-mappers';
import { datasourceCache, workspaceDatasourceCache } from '../resource-cache';
import { DatasourceResource } from './datasource-response.types';
import { HttpClient } from '@angular/common/http';
import { OrgTypes } from '@mode/shared/contract-common';
import { LocalStorageService } from '@mode/shared/util-js';

const PUBLIC_DS = '__MODE_PUBLIC__';

@Injectable({
  providedIn: 'root',
})
export class DatasourceClientService {
  constructor(
    private apiService: ApiService,
    private accountFetcher: AccountFetchService,
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  // Storing current requests to prevent redundant requests during race conditions
  private publicDatasourcesRequest: Observable<DatasourceDomain.Datasource[]> | null = null;
  private datasourcesRequests: { [username: string]: Observable<DatasourceDomain.Datasource[]> } = {};

  getPublicDatasources() {
    const cached = workspaceDatasourceCache.get(PUBLIC_DS);

    if (cached == null) {
      if (this.publicDatasourcesRequest) {
        return this.publicDatasourcesRequest;
      } else {
        this.publicDatasourcesRequest = from(
          this.apiService.getFromPath('/api', {
            includes: ['data_sources'],
          })
        ).pipe(
          map((resource) => getEmbeddedCollection<DatasourceResource>(resource, 'data_sources') || []),
          tap((resources) => workspaceDatasourceCache.set(PUBLIC_DS, resources)),
          map((resources) => resources.map((r) => toPublicDatasource(r))),
          finalize(() => (this.publicDatasourcesRequest = null))
        );

        return this.publicDatasourcesRequest;
      }
    } else {
      return of(cached).pipe(map((resources) => resources.map((r) => toPublicDatasource(r))));
    }
  }

  getDatasources(orgUsername: string) {
    const cached = workspaceDatasourceCache.get(orgUsername);

    if (cached == null) {
      if (this.datasourcesRequests[orgUsername]) {
        return this.datasourcesRequests[orgUsername];
      } else {
        return (this.datasourcesRequests[orgUsername] = this.accountFetcher.getAccountResource(orgUsername).pipe(
          switchMap((account) =>
            this.apiService
              .getResources<DatasourceResource>(account, 'data_sources', {
                includes: {
                  data_sources: 'new_data_source_schema_update',
                },
              })
              .then((resources) => ({
                resources,
                account,
              }))
          ),
          tap(({ resources }) => workspaceDatasourceCache.set(orgUsername, resources)),
          map(({ resources, account }) => resources.map((r) => toDatasource(r, account))),
          finalize(() => delete this.datasourcesRequests[orgUsername])
        ));
      }
    } else {
      return of(cached).pipe(
        switchMap((resources) =>
          this.accountFetcher
            .getAccountResource(orgUsername)
            .pipe(map((account) => resources.map((r) => toDatasource(r, account))))
        )
      );
    }
  }

  refreshDatasource(token: string): Observable<void> {
    const cached = datasourceCache.get(token);

    if (cached == null) {
      return throwError(new ResourceNotLoadedError(token, 'Datasource'));
    }

    const formEmbed = getEmbedded(cached, 'new_data_source_schema_update');
    if (formEmbed && formEmbed._forms?.['create']) {
      // Ignoring the result for now
      return from(this.apiService.submit(formEmbed._forms?.['create'], {})).pipe(map(() => undefined));
    } else {
      return throwError(new ResourceNotLoadedError(token, 'DatasourceSchemaUpdate'));
    }
  }

  fetchDataSourcesList(OrgId: string): Observable<OrgTypes.DataSourcesListResponse> {
    return this.httpClient
      .get<OrgTypes.DataSourcesListResponse>(`${OrgId}/data_sources/fetch`)
      .pipe(catchError((error) => throwError(error)));
  }

  inviteUser(orgName: string, params: OrgTypes.InviteRequestPayload): Observable<OrgTypes.InviteErrorResponse> {
    const url = `/api/${orgName}/invites`;
    return this.httpClient
      .post<OrgTypes.InviteErrorResponse>(url, params)
      .pipe(catchError((error) => throwError(error)));
  }

  getUserInvitePermission(orgID: string, username: string): Observable<OrgTypes.UserPermission> {
    const url = `/${orgID}/data_sources/user_can_invite/${username}`;
    return this.httpClient.get<OrgTypes.UserPermission>(url).pipe(catchError((error) => throwError(error)));
  }
}
